"use client";
import { Track } from "@/src/lib/tracking";

import { OspToast, SlotContent, deviceDetector } from "@ospweb/sdk-ui";
import { memo, useCallback, useEffect, useRef, useState } from "react";

import { cn } from "utils";
import { useSearchParams } from "next/navigation";
import { useSnapshot } from "valtio";
import { isEmpty } from "lodash-es";
import { rootStore } from "@store/rootStore";
import { Back } from "@components/mainHeader/Back";
import Subscription from "../components/Member/Subscription";
import { ProductType } from "../components/Member/CurrentPlan/constants";
import { ROUTER_PATH } from "@config/router";
import CurrentPlan from "../components/Member/CurrentPlan";
import { useMemberFeature } from "@hooks/useMemberFeature";
import { useIsPc } from "../hooks/useIsPc";
import { UpgradeSuccessModalCreateAble } from "../components/Payment/UpgradeSuccessModal/UpgradeSuccessModalCreateAble";
import Translations from "@translate/screens/Member";
import { I18nUtil } from "@utils/i18n";
import { orderIdByChannel, reportPurchaseGtm } from "@utils/member/purchaseGtm";
import { useMemberUser } from "@/src/hooks/useMemberUser";
import { openCancelSubscriptionPromptModal } from "@components/Member/SubscriptionMobilePricing/CancelSubscriptionPromptModalCallable";
import SubscriptionMobilePricing from "@components/Member/SubscriptionMobilePricing";
import { useGetListPayment } from "@/src/hooks/useGetListPayment";
import { useSubscriptionList } from "@/src/hooks/useSubscriptionList";
import { useInvalidateMemberQueries } from "@/src/hooks/useInvalidateMemberQueries";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { useGoalActivationCheckoutReturn } from "@/src/hooks/goal/useGoalActivationCheckoutReturn";
import { ONBOARDING_CHECKOUT_QUERY } from "@hooks/onboarding/onboardingCheckoutContinuation";
import { redirectToLoginPage } from "@features/login/redirectToLoginPage";
import { SubscriptionCapability } from "@features/api/memberApi";
import { useUpdateAutoRenew } from "@hooks/useSubscriptionManage";
import { usePaymentMethodReturn } from "@hooks/usePaymentMethodReturn";
import type { SubscriptionListItem } from "@components/Member/CurrentPlan/ManageBillingModal/types";
import {
  buildConfirmPendingMessage,
  useCheckoutReturnConfirm,
} from "@hooks/useCheckoutReturnConfirm";
import { CheckoutConfirmingOverlay } from "@components/Member/CheckoutConfirming/CheckoutConfirmingOverlay";
import { clearCheckoutStash } from "@utils/member/checkoutSessionStash";
import { channelRequiresReturnConfirm } from "@utils/member/checkoutChannel";
const I18n = I18nUtil.createScoped(Translations);

/** 等待 EdgenModalCreateAble.Root (lazyHydrate) 挂载后再调弹窗 */
const MODAL_ROOT_DELAY_MS = 1500;

// 定义组件接收的props类型
interface MemberProps {}

export const MemberKeys = {
  CURRENT_PLAN: "currentPlan",
  PRICING: "pricing",
};

function Member({}: MemberProps) {
  const { invalidateMemberQueriesAfterDelay } = useInvalidateMemberQueries();
  const router = useShellRouter();
  const { ownerInfo } = useSnapshot(rootStore.user);
  const ownerId = ownerInfo?.id ?? "";
  const searchParams = useSearchParams();
  const payment = searchParams.get("payment");
  const onboardingCheckout = searchParams.has(ONBOARDING_CHECKOUT_QUERY);
  const planId = searchParams.get("planId");
  // 回跳渠道标识：return_url 统一追加 `&channel=`（EDG-539 起 PayPal，EDG-552 起所有 web 渠道）；
  // 缺省仅见于改造前的旧链接，按 STRIPE_WEB 口径处理
  const channel = searchParams.get("channel");
  const predeterminedTab = searchParams.get("tab");
  const resumePurchasedGoal = useGoalActivationCheckoutReturn(
    ownerId,
    onboardingCheckout ? null : payment,
    router.replace,
  );
  const [isPC] = useIsPc();
  const initTab =
    predeterminedTab === MemberKeys.CURRENT_PLAN
      ? MemberKeys.CURRENT_PLAN
      : MemberKeys.PRICING;
  const [activeKey, setActiveKey] = useState<string>(initTab);
  const { isLoggedIn, isLoading, isFetching } = useSnapshot(rootStore.login);
  const {
    data: memberFeatureData,
    isTrial,
    isLoading: isLoadingFeature,
    trialList,
  } = useMemberFeature();
  const { data: userPlanInfo, isLoading: isLoadingUserPlanInfo } =
    useMemberUser();
  const isMemberCatalogLoading = isLoadingFeature || isLoadingUserPlanInfo;
  const { cancelSubscription } = useGetListPayment();
  // 用连续订阅列表作为“旧订阅”候选来源，后续用于引导用户去取消续费。
  const { data: subscriptionListItems } = useSubscriptionList({
    enabled: !!isLoggedIn,
  });
  // 防止 stripe success 回跳被重复消费（依赖变化导致 effect 再次触发）
  const handledStripeSuccessKeyRef = useRef<string | null>(null);
  const { isConfirming, run: runCheckoutConfirm } = useCheckoutReturnConfirm();
  const { updateAutoRenew } = useUpdateAutoRenew();
  // PC 换卡托管页回跳（?pmSession=done|cancel，F8）
  usePaymentMethodReturn(ROUTER_PATH.MEMBER);
  // 只在卸载时中止 confirm 轮询：回跳 effect 依赖 isFetching 等状态会反复重跑，不能在它的 cleanup 里 abort
  const confirmAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => confirmAbortRef.current?.abort(), []);
  const handleChange = useCallback(
    (changeKey: string) => {
      if (!isLoggedIn && changeKey === MemberKeys.CURRENT_PLAN) {
        redirectToLoginPage();
        return;
      }
      activeKey !== changeKey && setActiveKey(changeKey);
    },
    [activeKey, isLoggedIn],
  );

  useEffect(() => {
    // 未登录并且不在获取登录状态过程中
    if (!isLoggedIn && !isLoading && !isFetching) {
      handleChange(MemberKeys.PRICING);
    }
    // 只要是会员 就跳转 CurrentPlan
    if (userPlanInfo?.isMember && !predeterminedTab && handleChange) {
      handleChange(MemberKeys.CURRENT_PLAN);
    }
  }, [
    isLoggedIn,
    isLoading,
    isFetching,
    userPlanInfo?.isMember,
    predeterminedTab,
  ]);

  const invalidMemberData = useCallback(() => {
    void invalidateMemberQueriesAfterDelay();
  }, [invalidateMemberQueriesAfterDelay]);

  // 购买前订阅快照：
  // 后续是否弹「取消续订提示」需要基于“购买前”的可取消订阅数判断，
  // 不能使用购买后列表（购买成功后数据会刷新，可能导致判断偏移）。
  const getCancelableSubscriptionSnapshot = useCallback(() => {
    // 这里不按 canCancel 过滤：canCancel 是“当前设备可管理”的能力标记，
    // 业务判断需要的是“连续订阅项总数（购买前）”。
    const snapshotItems = subscriptionListItems || [];
    return {
      cancelableCount: snapshotItems.length,
      // 这里单独保留第一条 canCancel，用于按钮可跳转时的目标渠道/链接。
      firstCanCancelItem: snapshotItems.find((item) => item.canCancel),
    };
  }, [subscriptionListItems]);

  const openCancelAutoRenewPromptIfNeeded = useCallback(
    (params: {
      productId?: string | null;
      cancelableCountBeforePurchase: number;
      firstCanCancelItemBeforePurchase?: SubscriptionListItem;
    }) => {
      const {
        productId,
        cancelableCountBeforePurchase,
        firstCanCancelItemBeforePurchase,
      } = params;
      // 以“本次成功购买的产品”判定是否是 Lifetime，避免依赖 tab 切换后的 UI 状态。
      const purchasedPlan = productId
        ? memberFeatureData?.find((item) => item.id === productId)
        : undefined;
      const isLifetimePlan = purchasedPlan?.pay_cycle === "LIFETIME";
      // 需求规则：
      // 1) 买的是 Lifetime 且买前可取消订阅 > 0 -> 提示取消续订
      // 2) 买的非 Lifetime 且买前可取消订阅 > 1 -> 提示取消续订
      const shouldPromptCancelRenewal =
        isLifetimePlan && cancelableCountBeforePurchase > 0;
      // 暂不启用：非 Lifetime 且买前可取消订阅 > 1 时提示取消续订。
      // const shouldPromptForNonLifetime =
      //   !isLifetimePlan && cancelableCountBeforePurchase > 1;
      if (!shouldPromptCancelRenewal) {
        return;
      }

      const hasManageAction = !!firstCanCancelItemBeforePurchase;
      void openCancelSubscriptionPromptModal({
        actionLabel: hasManageAction
          ? I18n.t("Manage Subscriptions")
          : I18n.t("Get it"),
        onAction: () => {
          if (!firstCanCancelItemBeforePurchase) {
            return;
          }
          // 能力可直接关续费（AIRWALLEX）→ PATCH auto_renew=false（AC-40）；否则旧路径外跳门户 / 应用商店
          if (
            firstCanCancelItemBeforePurchase.capabilities?.includes(
              SubscriptionCapability.CANCEL_AT_PERIOD_END,
            )
          ) {
            void updateAutoRenew(firstCanCancelItemBeforePurchase, false);
            return;
          }
          void cancelSubscription({
            pay_channel: firstCanCancelItemBeforePurchase.pay_channel,
            links: firstCanCancelItemBeforePurchase.links,
          });
        },
      });
    },
    [memberFeatureData, cancelSubscription, updateAutoRenew],
  );

  const onAppPurchaseSuccess = useCallback(
    async (productId: string) => {
      try {
        // 先抓“购买前快照”，后续统一用该快照判断，避免被成功后的数据刷新干扰。
        const {
          cancelableCount: cancelableCountBeforePurchase,
          firstCanCancelItem: firstCanCancelItemBeforePurchase,
        } = getCancelableSubscriptionSnapshot();
        const plan = memberFeatureData?.find((item) => item.id === productId);
        const isBuyTrialProduct = plan?.type === ProductType.TRIAL;

        invalidMemberData();
        if (resumePurchasedGoal()) {
          return;
        }

        // GA4 `purchase` **不在这里发** —— 已收进 `purchaseInApp`（`useAppPurchaseFlow`），
        // 与 `begin_checkout` 同一层。在这里再发一次会让 `/member` 这个入口重复计一次转化。

        await new Promise((r) => setTimeout(r, 150));
        // 成功弹窗里存在“关闭按钮”和“Get started”两个关闭路径，二者都会触发回调。
        // 这里做一次性保护，保证“后置提示弹窗”只弹一次。
        let hasHandledAfterSuccessModal = false;
        const handleAfterSuccessModalClose = () => {
          if (hasHandledAfterSuccessModal) {
            return;
          }
          hasHandledAfterSuccessModal = true;
          openCancelAutoRenewPromptIfNeeded({
            productId,
            cancelableCountBeforePurchase,
            firstCanCancelItemBeforePurchase,
          });
        };
        void UpgradeSuccessModalCreateAble({
          isTrial: !!isBuyTrialProduct,
          onClose: handleAfterSuccessModalClose,
          onSuccessFunction: handleAfterSuccessModalClose,
        }).catch((modalError) => {
          Track.log(
            "warn",
            "Member",
            "UpgradeSuccessModalCreateAble failed (Root may not be ready)",
            { cause: String(modalError) },
          );
        });
      } catch (error) {
        Track.log("error", "Member", "onAppPurchaseSuccess error", {
          arg1: String(error),
          arg2: String(JSON.stringify(error)),
        });
        invalidMemberData();
      } finally {
      }
    },
    [
      resumePurchasedGoal,
      memberFeatureData,
      invalidMemberData,
      getCancelableSubscriptionSnapshot,
      openCancelAutoRenewPromptIfNeeded,
    ],
  );

  useEffect(() => {
    if (onboardingCheckout) {
      return;
    }
    if (payment === "cancel") {
      window.history.replaceState({}, "", ROUTER_PATH.MEMBER);
      if (planId) {
        clearCheckoutStash("checkout", planId);
        Track.event("checkout_result", {
          channel: channel ?? undefined,
          product_id: planId,
          result: "cancelled",
        });
      }
      return;
    }

    const stripeSuccessKey =
      payment === "success" && planId ? `${payment}_${planId}` : null;
    const initPaymentProcess = () => {
      if (!stripeSuccessKey) {
        return;
      }

      if (handledStripeSuccessKeyRef.current === stripeSuccessKey) {
        return;
      }

      // 等关键数据就绪后再弹窗
      if (
        !!ownerId &&
        !isLoading &&
        !isFetching &&
        !isLoadingFeature &&
        !isLoadingUserPlanInfo &&
        !isEmpty(memberFeatureData) &&
        !!planId
      ) {
        // Stripe 回跳分支同样使用“购买前快照”，保证规则与 App 内购分支一致。
        const {
          cancelableCount: cancelableCountBeforePurchase,
          firstCanCancelItem: firstCanCancelItemBeforePurchase,
        } = getCancelableSubscriptionSnapshot();
        const plan = memberFeatureData?.find((item) => item.id === planId);
        if (!plan) {
          return;
        }

        handledStripeSuccessKeyRef.current = stripeSuccessKey;
        // 成功流一旦消费，立即清理 URL 参数，避免后续依赖变化反复触发
        window.history.replaceState({}, "", ROUTER_PATH.MEMBER);
        const finishCheckoutSuccess = () => {
          // Stripe / PayPal 回跳即视为成功（渠道 webhook 兜底）；AIRWALLEX 只有 confirm 返回已生效才走到这里
          reportPurchaseGtm({
            // channel 缺省（旧 Stripe 链接/直链）落回 STRIPE_WEB，与现状一致
            orderId: orderIdByChannel(channel),
            plan,
          });
          invalidMemberData();
          if (resumePurchasedGoal()) {
            return;
          }
          // 3. 延迟一小段时间等待 EdgenModalCreateAble.Root 挂载，再弹出成功弹窗
          window.setTimeout(async () => {
            try {
              // 延迟刷新一次会员相关缓存，尽量覆盖支付成功后后端状态尚未落库完成的窗口。
              invalidMemberData();
              // 同 App 内购分支，统一做关闭回调去重，避免后置提示重复弹出。
              let hasHandledAfterSuccessModal = false;
              const handleAfterSuccessModalClose = () => {
                if (hasHandledAfterSuccessModal) {
                  return;
                }
                hasHandledAfterSuccessModal = true;
                openCancelAutoRenewPromptIfNeeded({
                  productId: planId,
                  cancelableCountBeforePurchase,
                  firstCanCancelItemBeforePurchase,
                });
              };
              await UpgradeSuccessModalCreateAble({
                isTrial: plan?.type === ProductType.TRIAL,
                onClose: handleAfterSuccessModalClose,
                onSuccessFunction: handleAfterSuccessModalClose,
              });
            } catch (modalError) {
              Track.log(
                "warn",
                "Member",
                "UpgradeSuccessModalCreateAble failed (stripe web)",
                { cause: String(modalError) },
              );
            }
          }, MODAL_ROOT_DELAY_MS);
        };
        if (!channelRequiresReturnConfirm(channel)) {
          finishCheckoutSuccess();
          return;
        }
        // 需主动确认的渠道（AIRWALLEX）：回跳 ≠ 生效，先主动确认（F4 遮罩期间不可操作）
        confirmAbortRef.current = new AbortController();
        void (async () => {
          const outcome = await runCheckoutConfirm({
            planId,
            channel,
            signal: confirmAbortRef.current?.signal,
          });
          if (confirmAbortRef.current?.signal.aborted) {
            return;
          }
          if (outcome.result !== "confirmed") {
            // pending_timeout / no_session（暂存丢失，如 iOS standalone PWA）：都没拿到「已生效」的确认 ——
            // 只提示「生效中」，从不报失败；不发 GTM purchase、不弹成功弹窗（AC-15 / AC-16），webhook 兜底后刷新会员态
            OspToast.info({ content: buildConfirmPendingMessage() });
            invalidateMemberQueriesAfterDelay();
            return;
          }
          finishCheckoutSuccess();
        })();
      }
    };
    const cleanup = initPaymentProcess();
    return cleanup;
  }, [
    onboardingCheckout,
    resumePurchasedGoal,
    ownerId,
    payment,
    isLoading,
    planId,
    channel,
    memberFeatureData,
    isLoadingFeature,
    isLoadingUserPlanInfo,
    isFetching,
    invalidMemberData,
    getCancelableSubscriptionSnapshot,
    openCancelAutoRenewPromptIfNeeded,
    runCheckoutConfirm,
    invalidateMemberQueriesAfterDelay,
  ]);

  return (
    <div className={"relative flex min-h-0 flex-1 flex-col"}>
      <CheckoutConfirmingOverlay open={isConfirming} />
      <SlotContent id="member-header-left">
        <div className="flex flex-row items-center justify-between w-full max-md:relative max-md:self-stretch">
          {deviceDetector.isMobile() && <Back className="z-13" />}
          <TabComponent activeKey={activeKey} handleChange={handleChange} />
        </div>
      </SlotContent>
      <div className="hidden md:block">
        <TabComponent activeKey={activeKey} handleChange={handleChange} />
      </div>
      {/* < */}
      <div role="tab-content" className="h-full min-h-0 flex flex-col pb-safe">
        <CurrentPlan
          handleChange={handleChange}
          className={cn({ hidden: activeKey !== MemberKeys.CURRENT_PLAN })}
          data={userPlanInfo}
          isLoading={isLoadingUserPlanInfo}
        />
        <Subscription
          handleChange={handleChange}
          className={cn({
            hidden: activeKey !== MemberKeys.PRICING || !isPC,
          })}
          userPlanInfo={userPlanInfo}
          isTrial={isTrial}
          isLoading={isMemberCatalogLoading || isConfirming}
          trialList={trialList}
          data={memberFeatureData}
          onAppPurchaseSuccess={onAppPurchaseSuccess}
        />
        {/*
          从 /member 页面入口直接分流移动端新 UI：
          - 大屏继续走旧 Subscription，确保现有桌面布局完全不变
          - 小屏只替换展示壳子，内部购买逻辑仍然复用旧 PurchaseActions
        */}
        <div
          className={cn("h-full min-h-0 flex-1", {
            hidden: activeKey !== MemberKeys.PRICING || isPC,
          })}
        >
          <SubscriptionMobilePricing
            userPlanInfo={userPlanInfo}
            isTrial={isTrial}
            isLoading={isMemberCatalogLoading || isConfirming}
            trialList={trialList}
            data={memberFeatureData}
            onAppPurchaseSuccess={onAppPurchaseSuccess}
            planIdParam={planId}
          />
        </div>
      </div>
    </div>
  );
}

const TabComponent = memo(
  ({
    activeKey,
    handleChange,
  }: {
    activeKey: string;
    handleChange: (key: string) => void;
  }) => {
    return (
      <tab-indicator
        className={cn(
          "flex flex-1 justify-center shrink-0 gap-6 sticky md:top-0 z-11",
          // H5: cancel MainHeader py-2 so underline sits on header border-b (PC uses md: path)
          "max-md:absolute max-md:left-0 max-md:right-0 max-md:-top-2 max-md:-bottom-2",
          "md:before:absolute md:before:content-[''] md:before:inset-0 md:before:bottom-0 md:before:border-b md:before:border-border_white_transparent10",
          "[--underline-color:var(--edgen-border\\_brand\\_solid)] [--animation-duration:0.2s] md:pt-5",
        )}
        effect="underline"
        enable-sticky-scroll={true}
      >
        <p
          data-openreplay-label="member_member_click"
          role="tab-item"
          className="tab_item py-2 md:h-10 h-13 button_l "
          data-actived={activeKey === MemberKeys.CURRENT_PLAN}
          onClick={() => handleChange(MemberKeys.CURRENT_PLAN)}
        >
          {I18n.t("Current Plan")}
        </p>
        <p
          data-openreplay-label="member_member_click"
          role="tab-item"
          className="tab_item py-2 md:h-10 h-13 button_l"
          data-actived={activeKey === MemberKeys.PRICING}
          onClick={() => handleChange(MemberKeys.PRICING)}
        >
          {I18n.t("Subscriptions")}
        </p>
      </tab-indicator>
    );
  },
);

TabComponent.displayName = "TabComponent";

export default Member;
